import { Crown, TrendingUp, Minus } from 'lucide-react';
import { ScrollArea } from '../../components/ui/scroll-area';
import EntityAvatar from '../../components/EntityAvatar';
import ScoreFloater from '../../components/ScoreFloater';
import { ENTITY_TYPE_COLORS, ENTITY_TYPE_LABELS } from './stageConfig';
import { formatFranc, formatFrancAdaptive } from '../../lib/utils';

/**
 * List gameplay view — extracted verbatim from StageTab.js (2026-02
 * StageTab gameplay sub-component split). Pure presentational: renders
 * the roster grouped by category with per-row price + live daily score.
 */
export const StageListView = ({
  slots, floatersByEntity, liveDaily, hasHugeFloater, onEntityClick,
}) => {
  return (
    <ScrollArea className="h-[400px]" data-testid="stage-list-view">
      <div className="space-y-1">
        {slots.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            No people drafted yet. Visit Explore to build your team!
          </div>
        )}
        {(() => {
          let lastCategory = '';
          return slots.map((slot) => {
            const entity = slot.entity;
            const typeColor = ENTITY_TYPE_COLORS[slot.entity_type] || '#888';
            const showHeader = slot.entity_type !== lastCategory;
            lastCategory = slot.entity_type;
            const myFloaters = floatersByEntity[slot.entity_id] || [];
            const flash = hasHugeFloater(slot.entity_id) ? '1' : undefined;
            const daily = liveDaily(slot);
            return (
              <div key={slot.entity_id}>
                {showHeader && (
                  <div className="flex items-center gap-2 px-2 pt-3 pb-1">
                    <div className="h-2 w-2 rounded-full" style={{ backgroundColor: typeColor }} />
                    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {ENTITY_TYPE_LABELS[slot.entity_type] || slot.entity_type}
                    </span>
                    <div className="flex-1 h-px bg-border/40" />
                  </div>
                )}
                <div
                  className="stage-list-row relative flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card px-4 py-3 cursor-pointer hover:shadow-sm hover:border-border/80 active:scale-[0.99] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${entity?.name || 'person'} details`}
                  data-testid={`stage-list-entity-${slot.entity_id}`}
                  data-flash={flash}
                  onClick={() => onEntityClick && onEntityClick(slot.entity_id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEntityClick && onEntityClick(slot.entity_id); } }}
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <EntityAvatar
                        entity={entity || { entity_type: slot.entity_type }}
                        size={40}
                        className={entity?.metadata?.death_date ? 'grayscale opacity-70' : ''}
                      />
                      {slot.is_captain && (
                        <Crown className="absolute -top-1 -right-1 h-3.5 w-3.5 text-[hsl(43,74%,52%)]" fill="currentColor" />
                      )}
                      {/* Live floaters anchor to the avatar */}
                      {myFloaters.map((f) => (
                        <ScoreFloater key={f.id} points={f.points} source={f.source} event_type={f.event_type} emoji={f.emoji} visual={f.visual} />
                      ))}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium">{entity?.name || 'Unknown'}</p>
                        {entity?.metadata?.death_date && (
                          <span
                            className="text-[9px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground border border-border/60"
                            data-testid={`stage-list-rip-${slot.entity_id}`}
                            title={`Deceased ${entity.metadata.death_date}`}
                          >
                            ✟ RIP
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {ENTITY_TYPE_LABELS[slot.entity_type] || slot.entity_type}
                        {entity?.metadata?.country && ` • ${entity.metadata.country}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums">
                        {formatFranc(entity?.pricing?.price)}
                      </p>
                      <div className="flex items-center gap-1">
                        {daily > 0 ? (
                          <span className="badge-positive text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5 tabular-nums">
                            <TrendingUp className="h-2.5 w-2.5" />
                            +{formatFrancAdaptive(daily)}
                          </span>
                        ) : (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-0.5">
                            <Minus className="h-2.5 w-2.5" /> 0
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          });
        })()}
      </div>
    </ScrollArea>
  );
};
